import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi, useAuth } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { AssociatedContactType, AssociatedTeamMember } from '../../../shared/models';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface DeleteDataRoomTeamMemberDialogProps {
	dataRoomGuid: string | undefined;
	member: AssociatedTeamMember | null;
	close: () => void;
}

export const DeleteDataRoomTeamMemberDialog: React.FC<DeleteDataRoomTeamMemberDialogProps> = ({
	dataRoomGuid,
	member,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { currentUser } = useAuth();

	const onDeleteConfirmation = () => {
		if (member !== null) deleteTeamMember(member);
	};

	const { mutate: deleteTeamMember } = useMutation<void, unknown, AssociatedTeamMember>(
		memberToDelete =>
			getResponseData(
				api.delete<void>(getDeleteApiUrl(dataRoomGuid, memberToDelete.guid, memberToDelete.contactType))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				Promise.all([
					client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.LenderByUser(dataRoomGuid, currentUser?.userGuid)),
				]).finally(() => {
					snackbar.enqueueSnackbar('Team Member Deleted.', { variant: 'success' });
					close();
				});
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem deleting the team member.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={member !== null} onClose={close}>
			<DialogTitle>Confirm Delete Team Member</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to remove access for {member?.firstName} {member?.lastName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

function getDeleteApiUrl(
	dataRoomGuid: string | undefined,
	memberGuid: string | undefined,
	contactType: AssociatedContactType
): string {
	if (contactType == 'DataRoomUser') {
		return CerebroApiEndpoints.TeamMembers.AssociatedSpecific(dataRoomGuid, memberGuid);
	}
	return CerebroApiEndpoints.TeamMembers.Specific(dataRoomGuid, memberGuid);
}
